import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Lock, Eye, Phone, Mail, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <section ref={ref} className="pt-36 pb-16 bg-gradient-to-b from-olive-darkest to-olive-dark">
        <div className="max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="bg-gold/20 p-4 rounded-full">
                <Shield className="w-12 h-12 text-cream" />
              </div>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-cream mb-4">
              Privacy Policy
            </h1>
            <div className="w-20 h-0.5 bg-gold mx-auto mb-4"></div>
            <p className="text-cream/80 text-base sm:text-lg max-w-2xl mx-auto">
              Your privacy and data security are our top priorities
            </p>
            <p className="text-cream/60 text-sm mt-4">
              Last Updated: January 28, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-lg shadow-md p-8 space-y-8"
          >
            {/* Introduction */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                Introduction
              </h2>
              <p className="text-text-dark leading-relaxed">
                Century Longevity & Aesthetics is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                you visit our website or use our services. Please read this privacy policy carefully.
              </p>
            </div>

            {/* Information We Collect */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-olive-darkest/10 p-2 rounded">
                  <Eye className="w-5 h-5 text-olive-darkest" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-olive-darkest">
                  Information We Collect
                </h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-olive-darkest mb-2">Personal Information</h3>
                  <p className="text-text-dark leading-relaxed">
                    We may collect personal information that you provide to us, including:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-text-dark ml-4">
                    <li>Name, email address, and phone number</li>
                    <li>Appointment and service information</li>
                    <li>Medical history and health information (when applicable)</li>
                    <li>Payment and billing information</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-olive-darkest mb-2">Automatically Collected Information</h3>
                  <p className="text-text-dark leading-relaxed">
                    When you visit our website, we may automatically collect certain information about your device, including:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-text-dark ml-4">
                    <li>IP address and browser type</li>
                    <li>Operating system and device information</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website addresses</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                How We Use Your Information
              </h2>
              <p className="text-text-dark leading-relaxed mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-dark ml-4">
                <li>Provide, operate, and maintain our services</li>
                <li>Process appointments and communicate with you</li>
                <li>Send you appointment reminders and service updates</li>
                <li>Improve our website and service offerings</li>
                <li>Process payments and prevent fraud</li>
                <li>Comply with legal obligations and HIPAA requirements</li>
                <li>Send marketing communications (with your consent)</li>
              </ul>
            </div>

            {/* Mobile Information & SMS */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-olive-darkest/10 p-2 rounded">
                  <Phone className="w-5 h-5 text-olive-darkest" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-olive-darkest">
                  Mobile Information & Communications
                </h2>
              </div>
              
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-6 space-y-3">
                <p className="text-text-dark leading-relaxed font-semibold">
                  Important Notice About Phone Communications:
                </p>
                <p className="text-text-dark leading-relaxed">
                  <strong>We do not share, sell, or rent your mobile phone number or personally identifiable 
                  information with third parties or affiliates for marketing or promotional purposes.</strong>
                </p>
                <p className="text-text-dark leading-relaxed">
                  When you provide your phone number through our contact form, you consent to receive callback 
                  communications from Century Longevity & Aesthetics regarding your inquiry. We may contact you 
                  via phone call or text message. We do not automatically send marketing or promotional messages. 
                  Standard message and data rates may apply if contacted via text.
                </p>
                <p className="text-text-dark leading-relaxed">
                  By entering your phone number on our website, you are agreeing to receive text messages from us. 
                  Message frequency may vary. Message & Data rates may apply. Reply HELP for more information. 
                  You can reply STOP to opt-out.
                </p>
                <p className="text-text-dark leading-relaxed">
                  <strong>How to Opt-Out:</strong> You can opt-out of text message communications at any time by replying 
                  STOP or UNSUBSCRIBE to any message. For help, reply HELP or contact us directly at (332) 529-4781 
                  or centuryaesthetics@gmail.com.
                </p>
              </div>
            </div>

            {/* Information Sharing */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-olive-darkest/10 p-2 rounded">
                  <Lock className="w-5 h-5 text-olive-darkest" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-olive-darkest">
                  How We Share Your Information
                </h2>
              </div>
              
              <p className="text-text-dark leading-relaxed mb-3">
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-dark ml-4">
                <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform 
                services on our behalf (e.g., payment processing, appointment scheduling)</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect 
                our rights and safety</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, 
                your information may be transferred</li>
                <li><strong>With Your Consent:</strong> We may share information for other purposes with your explicit consent</li>
              </ul>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                Data Security
              </h2>
              <p className="text-text-dark leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. However, no method 
                of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee 
                absolute security.
              </p>
            </div>

            {/* HIPAA Compliance */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                HIPAA Compliance
              </h2>
              <p className="text-text-dark leading-relaxed">
                As a medical practice, we comply with the Health Insurance Portability and Accountability Act (HIPAA). 
                Protected health information (PHI) is handled in accordance with HIPAA regulations. For more information 
                about how we protect your medical information, please request a copy of our HIPAA Notice of Privacy Practices.
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                Your Privacy Rights
              </h2>
              <p className="text-text-dark leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-dark ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate or incomplete information</li>
                <li>Request deletion of your personal information (subject to legal requirements)</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent for SMS communications at any time</li>
              </ul>
            </div>

            {/* Website Tracking */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                Website Tracking and Analytics
              </h2>
              <p className="text-text-dark leading-relaxed">
                Our website is a frontend application that does not use cookies or persistent tracking technologies. 
                We do not collect or store browsing data, behavioral analytics, or use third-party tracking scripts. 
                The only backend functionality is our contact form, which uses EmailJS to send your inquiries directly 
                to us via email.
              </p>
            </div>

            {/* Third-Party Links */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                Third-Party Links
              </h2>
              <p className="text-text-dark leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy 
                practices or content of these external sites. We encourage you to review the privacy policies 
                of any third-party sites you visit.
              </p>
            </div>

            {/* Children's Privacy */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                Children's Privacy
              </h2>
              <p className="text-text-dark leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect 
                personal information from children. If you become aware that a child has provided us with personal 
                information, please contact us immediately.
              </p>
            </div>

            {/* Changes to Privacy Policy */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-olive-darkest mb-4">
                Changes to This Privacy Policy
              </h2>
              <p className="text-text-dark leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to 
                review this Privacy Policy periodically.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-olive-darkest/5 rounded-lg p-6 border border-olive-darkest/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-olive-darkest p-2 rounded">
                  <Mail className="w-5 h-5 text-cream" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-olive-darkest">
                  Contact Us
                </h2>
              </div>
              
              <p className="text-text-dark leading-relaxed mb-4">
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-olive-dark" />
                  <p className="text-text-dark text-sm">
                    70-64 Yellowstone Boulevard Unit D2, Forest Hills, NY 11375
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-olive-dark" />
                  <a href="tel:3325294781" className="text-text-dark text-sm hover:text-gold transition-colors">
                    (332) 529-4781
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-olive-dark" />
                  <a href="mailto:centuryaesthetics@gmail.com" className="text-text-dark text-sm hover:text-gold transition-colors">
                    centuryaesthetics@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;